import axios from 'smg/axios';
import * as carts from 'smg/customer-api/carts';
import { HandleRequestError } from 'smg/errors';
import Modal from 'smg/modal';
import * as models from 'smg/models';
import * as toast from 'smg/toast';
import { UUID } from 'smg/uuid';
import { Relref } from './hugo';

let cartPrompted: boolean = false;

export const GetCart = carts.GetCart;
export const GetCartID = carts.GetCartID;
export const GetCartCount = carts.GetCartCount;
export const DeleteCart = carts.DeleteCart;
export type CartResponse = carts.CartResponse;
export type TaxAddress = carts.TaxAddress;

export declare interface AddCartItemOpts {
    promptToCheckout?: boolean;
    hideCartLink?: boolean;
    showToast?: boolean;
    newCart?: boolean;
}

const defaultAddCartItemOpts = {
    promptToCheckout: true,
    hideCartLink: false,
    showToast: true,
    newCart: false,
};

export async function AddCartItem(cartItems: carts.AddCartItemParams | carts.AddCartItemParams[], opts: AddCartItemOpts = defaultAddCartItemOpts) {
    const promptToCheckout = opts.promptToCheckout ?? defaultAddCartItemOpts.promptToCheckout;
    const hideCartLink = opts.hideCartLink ?? defaultAddCartItemOpts.hideCartLink;
    const showToast = opts.showToast ?? defaultAddCartItemOpts.showToast;
    const newCart = opts.newCart ?? defaultAddCartItemOpts.newCart;

    return await carts.AddCartItems(cartItems, newCart).
        then(resp => {
            if (showToast) {
                toast.Success(`Added to cart! ${hideCartLink ? '' : `<a href='${Relref('/cart')}' class="text-white text-decoration-underline">Go to cart</a>`}`);
            }

            if (promptToCheckout && !cartPrompted) {
                new Modal({
                    title: 'Quote added to your cart!',
                    text: 'Ready to checkout?',
                    confirmText: 'Go to cart',
                    cancelText: 'Keep shopping',
                    onSubmit: () => window.location.href = Relref('/cart'),
                });
                cartPrompted = true;
            }

            if (newCart) {
                ResetCartCount();
            }

            SetCartCount(resp.cartItemsCount);
        }).catch(HandleRequestError);
}

export async function DeleteCartItem(cartItemID: string, quantity: number = 1) {
    return axios.delete(`/cart-items/${cartItemID}`).then(() => {
        const undoLinkID = `undo-${UUID()}`;
        const t = toast.Success(`Removed from cart. <a href="${Relref('/cart')}" class="text-white text-decoration-underline" id="${undoLinkID}">Undo</a>`);

        const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
        undo.addEventListener('click', e => {
            e.preventDefault();

            void RestoreCartItem(cartItemID, quantity);

            t?.dispose();
        });

        DecrementCartCount(quantity);
    });
}

export async function RestoreCartItem(cartItemID: string, quantity: number = 1) {
    return axios.patch(`/cart-items/${cartItemID}/active`, {
        'active': true,
    }).then(() => IncrementCartCount(quantity));
}

export declare interface CartResp {
    cart: models.Cart;
    subtotal: number;
    discount: number;
    shipping: number;
    taxAmount: number;
    taxRate: number;
    total: number;
}

let cartCount: number = 0;
let navCartLink: HTMLElement | undefined;
let navCartCount: NodeListOf<HTMLElement> | undefined;
let navCartIcon: HTMLElement | undefined;

function initCarNavLinks() {
    if (!navCartLink || !navCartCount || !navCartIcon) {
        navCartLink = document.querySelector('#nav-cart-link') as HTMLElement;
        navCartCount = document.querySelectorAll('.nav-cart-count');
        navCartIcon = document.querySelector('#shopping-cart-icon') as HTMLElement;
    }
}

export function SetCartCount(qty: number) {
    initCarNavLinks();

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(qty);
        });
    }
}

export function IncrementCartCount(add: number = 1) {
    initCarNavLinks();

    cartCount += add;

    navCartIcon?.classList.remove('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function DecrementCartCount(minus: number = 1) {
    initCarNavLinks();

    cartCount -= minus;
    cartCount = Math.max(cartCount, 0);

    navCartIcon?.classList.add('opacity-75');
    if (navCartCount) {
        navCartCount.forEach(el => {
            if (cartCount < 1) {
                el.classList.add('d-none');
                return;
            }
            el.classList.remove('d-none');
            el.textContent = String(cartCount);
        });
    }
}

export function ResetCartCount() {
    DecrementCartCount(cartCount);
}

export function GetCurrentCartCount() {
    return cartCount;
}